import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ApplicationState } from '../../../store/rootReducer';
import { fetchBlueprintByIdThunk } from '../../../store/blueprints/blueprintsActions';
import BlueprintTransactionsTable from './BlueprintTransactionsTable';
import BlueprintThumb from './BlueprintThumb';
import SimpleLoader from '../../../components/loaders/SimpleLoader';
import BlazeDataIcon from '../../../components/icons/BlazeDataIcon';
import { hasUserAdminRoles } from '../../../utils/bzmUserConfigUtil';
import { loadDevOptions } from '../../../utils/localStorageUtil';
import { confirm } from '../../../components/ui/modal/BlazeConfirmationModal';
import { getShortString } from '../../../utils/miscUtil';
import styles from './BlueprintDetailView.module.css';

const DESCRIPTION_LIMIT = 180;

interface Props {
  id: string;
  onClose: () => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onAddToCatalog: (id: string) => void;
  closeLabel?: string;
  showAddButton?: boolean;
  closeAsIcon?: boolean;
}

const BlueprintDetailContent = ({
  id,
  onClose,
  onEdit,
  onDelete,
  onAddToCatalog,
  closeLabel,
  showAddButton = true,
  closeAsIcon = false,
}: Props) => {
  const { t } = useTranslation('blueprints');
  const closeText = closeLabel || t('detail.back');
  const dispatch = useDispatch();
  const state = useSelector((s: ApplicationState) => s.blueprints);
  const blazeUserConfig = useSelector((s: ApplicationState) => s.blazeUserConfig.blazeUserConfig);
  const isAdmin = hasUserAdminRoles(blazeUserConfig);
  const blueprintEditEnabled = loadDevOptions()?.enableBlueprintEdit === true;
  const [descExpanded, setDescExpanded] = useState(false);

  useEffect(() => {
    dispatch(fetchBlueprintByIdThunk(id) as never);
  }, [dispatch, id]);

  const blueprint = state.blueprints.find(b => b.id === id) || null;

  if (state.detailLoading && !blueprint) {
    return (
      <div className="card">
        <div className="card-body text-muted">{t('detail.loading')}</div>
      </div>
    );
  }

  if (!blueprint) {
    return (
      <div className="card">
        <div className="card-body">
          <button className="btn btn-link p-0 mb-3" onClick={onClose}>
            {closeText}
          </button>
          <p>{t('detail.notFound')}</p>
        </div>
      </div>
    );
  }

  const isCustom = blueprint.source === 'CUSTOM';
  // transactions[] is only populated by the detail fetch; fall back to the list's count while it loads.
  const txnCount = blueprint.transactionCount ?? blueprint.transactions.length;

  const metaParts: string[] = [];
  // This will be used later with custom blueprints, but for now we don't want to show it since all blueprints are built-in.
  // metaParts.push(isCustom ? t('detail.source.custom') : t('detail.source.builtIn'));

  metaParts.push(t('detail.transactions', { count: txnCount }));
  // if (blueprint.author) {
  //   metaParts.push(t('detail.by', { author: blueprint.author }));
  // }
  if (blueprint.createdDate) {
    metaParts.push(t('detail.created', { date: blueprint.createdDate }));
  }

  const isLongDescription = (blueprint.description?.length ?? 0) > DESCRIPTION_LIMIT;
  // The toggle also reveals the metadata line, so offer it whenever there is metadata or an overflowing description.
  const canExpand = isLongDescription || metaParts.length > 0;

  return (
    <>
      <div className={`${styles.card} mb-3`}>
        <div className={styles.hero}>
          <BlueprintThumb
            name={blueprint.name}
            thumbnail={blueprint.thumbnail}
            fontSize={22}
            style={{ width: 72, height: 72, borderRadius: 8 }}
          />
          <div className="flex-grow-1">
            <div className="d-flex align-items-center mb-2" style={{ gap: 8 }}>
              <h3 className="mb-0">{blueprint.name}</h3>
              {blueprint.hasBlazeData && <BlazeDataIcon size={20} title="Includes a BlazeData dataset" />}
            </div>
            {blueprint.tags.length > 0 && (
              <div className={`${styles.tags} mb-2`}>
                {blueprint.tags.map(tag => (
                  <span key={tag} className={styles.tag} title={tag}>
                    {tag}
                  </span>
                ))}
              </div>
            )}
            {blueprint.description && (
              <p className="text-muted mb-0">
                {descExpanded || !isLongDescription
                  ? blueprint.description
                  : getShortString(blueprint.description, DESCRIPTION_LIMIT)}
              </p>
            )}
            {descExpanded && metaParts.length > 0 && <div className={styles.meta}>{metaParts.join(' · ')}</div>}
            {canExpand && (
              <button
                type="button"
                className="btn btn-link btn-no-hover p-0 mt-1 align-baseline"
                onClick={() => setDescExpanded(e => !e)}
              >
                {descExpanded ? t('detail.showLess') : t('detail.showMore')}
              </button>
            )}
          </div>
          <div className={styles.actions}>
            {closeAsIcon ? (
              <button
                type="button"
                className={`close ${styles.closeIcon}`}
                aria-label={closeText}
                title={closeText}
                onClick={onClose}
              >
                <span aria-hidden="true">&times;</span>
              </button>
            ) : (
              <button type="button" className="btn btn-link btn-no-hover" onClick={onClose}>
                {closeText}
              </button>
            )}
            {isAdmin && blueprintEditEnabled && (
              <button type="button" className="btn btn-secondary" onClick={() => onEdit(blueprint.id)}>
                {t('detail.edit')}
              </button>
            )}
            {isAdmin && blueprintEditEnabled && (
              <button
                type="button"
                className="btn btn-danger"
                onClick={() =>
                  confirm({
                    title: t('detail.deleteConfirmTitle'),
                    confirmation: t('detail.deleteConfirm'),
                  }).then(() => onDelete(blueprint.id))
                }
              >
                {t('detail.delete')}
              </button>
            )}
            {showAddButton && (
              <button type="button" className="btn btn-primary" onClick={() => onAddToCatalog(blueprint.id)}>
                {t('detail.add')}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className={styles.card}>
        <div className={styles.tableHeader}>{t('detail.transactions', { count: txnCount })}</div>
        <div className={styles.tableWrap}>
          {state.detailLoading ? (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 120 }}>
              <SimpleLoader spinnerFirst loadingText={t('detail.loading')} />
            </div>
          ) : (
            <BlueprintTransactionsTable transactions={blueprint.transactions} />
          )}
        </div>
      </div>
    </>
  );
};

export default BlueprintDetailContent;
